import { List } from 'immutable';
import React, { ReactNode, useContext, useEffect } from 'react';
import { Ability } from '../../constants/enums/Ability';
import { isRestricted } from '../../utils/auth';
import useUnauthenticatedRedirect from 'app/components/auth/useUnauthenticatedRedirect';
import RedirectingPage from '../pages/RedirectingPage';
import { PageRestrictedError } from 'app/components/ErrorBoundaryPage';

const AuthRestrictedContext = React.createContext(false);
export const AuthRestrictedProvider = AuthRestrictedContext.Provider;
export const useIsAuthRestricted = () => useContext(AuthRestrictedContext);

interface IProps {
  alphaOnly?: boolean;
  allow?: List<Ability> | Ability[];
  children: ReactNode;
  isAuthenticated: boolean;
  userAbilities?: List<Ability> | Ability[];
  fallbackComponent?: React.ComponentType;
}

export const RestrictPage: React.FC<IProps>  = ({
  isAuthenticated,
  alphaOnly,
  allow,
  children,
  userAbilities,
}) => {
  const unauthenticatedRedirect = useUnauthenticatedRedirect();

  useEffect(() => {
    // Only redirect to the login page if the user is not logged in, so that we
    // do not cause a redirect loop.
    if (!isAuthenticated) {
      unauthenticatedRedirect();
    }
  }, [isAuthenticated, unauthenticatedRedirect]);

  if (!isAuthenticated) {
    // We don't need to render a proper page, because we will redirect anyway.
    return (
      <RedirectingPage />
    );
  } else if (isRestricted(allow, isAuthenticated, userAbilities, alphaOnly)) {
    // Will cause <ErrorBoundaryPage> to render a 403 page
    throw new PageRestrictedError();
  } else {
    return (
      <AuthRestrictedProvider value>
        {children}
      </AuthRestrictedProvider>
    );
  }
};

export default RestrictPage;
